import { Injectable } from '@angular/core';
import { formatDate } from 'date-fns';

import { EffortUI } from '../models/effort';
import { Project, TimeType, TypesUI } from '../models/project';
import { TimeSheet, TimesheetStatus } from '../models/time-entry';
import { ProjectTimeTypeShort, UIPreference } from '../models/ui-preferences';
import { TimeDateUtils } from './date-utils';
import { isLeaveProjectVisible, isOfficialLeaveCell, withImportedLeaveProjects } from '../services/official-leave.service';

export interface TimesheetProjectViewResult {
  projects: Project[];
  shadowProjects: Project[];
  effortUI: { [id: number]: EffortUI };
  typesUI: Map<number, TypesUI>;
  status?: TimesheetStatus;
  reviewerComments?: string;
  isReadonly: boolean;
  isSubmitted: boolean;
  isApproved: boolean;
  isForReview: boolean;
}

export interface BuildTimesheetProjectViewOptions {
  projects: Project[];
  timesheet: TimeSheet;
  uiPreference: UIPreference;
  weekDays: string[];
  previousProjects: Project[];
  typesUI: Map<number, TypesUI>;
  currentWeekStart?: string;
  preservePreviousChanges: boolean;
}

@Injectable()
export class TimesheetProjectViewService {
  buildView(options: BuildTimesheetProjectViewOptions): TimesheetProjectViewResult {
    const status = options.timesheet.status;
    const isReadonly =
      status === TimesheetStatus.Approved || status === TimesheetStatus.Submitted;
    const isSubmitted =
      status !== undefined &&
      status !== null &&
      status !== TimesheetStatus.Unsubmitted;
    const isApproved = status === TimesheetStatus.Approved;
    const isForReview = status === TimesheetStatus.Review;
    const typesUI = new Map(options.typesUI);
    const visibleProjects = withImportedLeaveProjects(options.projects).filter(project =>
      isLeaveProjectVisible(project, options.timesheet.weekStart));
    let projects = this.virtualizeProjects(visibleProjects, options.timesheet);
    projects = this.prependPinned(
      projects,
      options.uiPreference.timesheet?.pinnedProjectTimeTypes ?? [],
    );

    const effortUI = this.createEffortUI(
      options.uiPreference,
      typesUI,
      isReadonly,
    );
    const shadowProjects = this.createShadowProjects(projects);
    this.applyTimesheetEntries(projects, shadowProjects, options.timesheet, options.weekDays);
    this.expandReviewedAndSubmittedRows(
      projects,
      effortUI,
      typesUI,
      isSubmitted,
      options.timesheet.weekStart,
      options.currentWeekStart,
    );

    if (options.preservePreviousChanges) {
      this.restorePreviousChanges(projects, options.previousProjects);
    }

    return {
      projects,
      shadowProjects,
      effortUI,
      typesUI,
      status,
      reviewerComments: options.timesheet.reviewerComments,
      isReadonly,
      isSubmitted,
      isApproved,
      isForReview,
    };
  }

  daysWithHours(timesheet: TimeSheet): string[] {
    return [
      ...new Set(
        timesheet.timeEntries?.map((te) =>
          formatDate(Date.parse(te.dateEntry!), 'yyyy-MM-dd'),
        ),
      ),
    ];
  }

  isNextGroup(projects: Project[], i: number): boolean {
    const a = projects[i - 1];
    const b = projects[i];
    if (b.ui.groupName == 'Pinned') return false;
    if (i == 0) return true;
    return a.ui.groupName !== b.ui.groupName;
  }

  firstAndPinned(projects: Project[], i: number): boolean {
    return i == 0 && projects[0]?.ui.groupName == 'Pinned';
  }

  anyPinned(projects: Project[]): boolean {
    return projects?.some((p) => p.ui.groupName == 'Pinned') ?? false;
  }

  isNextProjectWithTypes(
    projects: Project[],
    i: number,
    isSubmitted: boolean,
    isForReview: boolean,
  ): boolean {
    if (projects[i].ui.groupName == 'Pinned') return false;

    const isNotReadonlyMode = !(isSubmitted && !isForReview);
    const isDifferentFromPrevious = i == 0 || projects[i - 1].id !== projects[i].id;
    const isOneOfMany =
      i < projects.length - 1 && projects[i].id == projects[i + 1].id;
    return isNotReadonlyMode && isDifferentFromPrevious && isOneOfMany;
  }

  projectHasHours(project: Project, weekDays: string[]): boolean {
    return weekDays.reduce(
      (v, day) =>
        v ||
        Number(parseFloat(project.hours[day] ? project.hours[day] : '0').toFixed(1)) !=
          0,
      false,
    );
  }

  isGrouped(projects: Project[], project: Project): boolean {
    const i = projects.findIndex((p) => p == project);
    return (
      (i > 0 && projects[i - 1].id == project.id) ||
      (i < projects.length - 1 && projects[i + 1].id == project.id)
    );
  }

  isExpanded(
    projects: Project[],
    effortUI: { [id: number]: EffortUI },
    typesUI: Map<number, TypesUI>,
    weekDays: string[],
    project: Project,
    isTypesHeader: boolean,
    isReadonly: boolean,
  ): boolean {
    if (project.ui.groupName == 'Pinned') return true;

    if (isReadonly) {
      if (isOfficialLeaveCell(project, weekDays[weekDays.length - 1] ?? '')) return true;
      const projHasData = (proj: Project) =>
        !!weekDays.find(
          (d) => parseFloat(proj.hours[d]) > 0 || !!proj.notes[d]?.length,
        );
      const projExpanded = projHasData(project);
      const effortExpanded =
        this.isGrouped(projects, project) &&
        projects.find(
          (p) =>
            p.effortId == project.effortId &&
            p.ui.groupName != 'Pinned' &&
            projHasData(p),
        );
      return projExpanded || !!effortExpanded;
    }

    effortUI[project.effort.id] ??= {};
    if (!this.isGrouped(projects, project) || isTypesHeader) {
      return !!effortUI[project.effort.id].isExpanded;
    }

    return (
      !!effortUI[project.effort.id]?.isExpanded &&
      this.isExpandedTypes(typesUI, project)
    );
  }

  isExpandedTypes(typesUI: Map<number, TypesUI>, project: Project): boolean {
    let uistate = typesUI.get(project.id!);
    if (!uistate) {
      uistate = {};
      typesUI.set(project.id!, uistate);
    }
    return !!uistate.isExpanded;
  }

  showTimeType(projects: Project[], project: Project): boolean {
    if (project?.ui.groupName == 'Pinned') return true;
    const membersPinned = projects.filter(
      (p) => project.id == p.id && p.ui.groupName == 'Pinned',
    );
    if (membersPinned.length) return true;
    if (project.ui.timeTypeId == 1 && !this.isGrouped(projects, project)) return true;
    return false;
  }

  private virtualizeProjects(projects: Project[], timesheet: TimeSheet): Project[] {
    return projects.flatMap((p) => {
      type OptionalTT = TimeType & {
        id: undefined | number;
        isLegacy?: boolean;
        isVirtual?: boolean;
        isSingle?: boolean;
      };
      const currentTypes = p.projectTimeTypes.map(
        (ptt) =>
          <OptionalTT>{
            ...ptt.timeType,
            isVirtual: true,
            isSingle: p.projectTimeTypes.length == 1,
          },
      );
      if (!p.projectTimeTypes.length) {
        currentTypes.push(<OptionalTT>{ name: 'Non-billable', id: 2 });
      }

      const removed =
        timesheet.timeEntries
          ?.filter(
            (e) =>
              e.projectId == p.id &&
              e.timeTypeId &&
              !currentTypes.find((tt) => tt.id == e.timeTypeId) &&
              e.recordedTime,
          )
          .map(
            (te) =>
              <OptionalTT>{
                ...te.timeType,
                isLegacy: true,
                isVirtual: true,
              },
          )
          .filter((v, i, arr) => arr.findIndex((a) => a.id == v.id) === i) ??
        [];

      const all = currentTypes
        .concat(removed)
        .filter((v, i, arr) => arr.findIndex((a) => a.id == v.id) === i);
      return all.map((tt) =>
        this.createViewModel(
          p,
          `${p.id}_${tt.id}`,
          tt.name,
          tt.id,
          !!tt.isVirtual,
          tt.isLegacy,
          tt.isSingle,
          all.length,
        ),
      );
    });
  }

  private createViewModel(
    project: Project,
    virtualId: string,
    virtualTitle: string | null = null,
    timeTypeId: number | undefined,
    isVirtual: boolean,
    isLegacy = false,
    isSingle = false,
    count = 0,
  ): Project {
    const vmp = structuredClone(project);
    vmp.hours = {};
    vmp.notes = {};
    vmp.review = {};
    vmp.ui = {
      groupName: project.effort.title,
      virtualId,
      virtualTitle,
      timeTypeId,
      isLegacy,
      isVirtual,
      isSingle,
      expanded: false,
      count,
    };
    return vmp;
  }

  private prependPinned(
    projects: Project[],
    pinnedProjectTimeTypes: ProjectTimeTypeShort[],
  ): Project[] {
    const pinned = [];
    let remaining = [...projects];
    for (let project of projects) {
      const key = <ProjectTimeTypeShort>{
        projectId: project.id,
        timeTypeId: project.ui.timeTypeId,
      };
      const pinning = pinnedProjectTimeTypes.find(
        (ptt) =>
          ptt.projectId == key.projectId && ptt.timeTypeId == key.timeTypeId,
      );
      if (pinning) {
        project.ui.groupName = 'Pinned';
        project.ui.isPinned = true;
        pinned.push(project);
        remaining = remaining.filter((p) => p != project);
      }
    }
    return pinned.concat(remaining);
  }

  private createEffortUI(
    uiPreference: UIPreference,
    typesUI: Map<number, TypesUI>,
    isReadonly: boolean,
  ): { [id: number]: EffortUI } {
    if (isReadonly || !uiPreference.timesheet) return {};

    const effortUI =
      uiPreference.timesheet.expandedEffortIds?.reduce(
        (v, c) => {
          v[c] = { isExpanded: true };
          return v;
        },
        <{ [id: number]: EffortUI }>{},
      ) ?? {};
    uiPreference.timesheet.expandedProjectIds?.forEach((id) => {
      let p = typesUI.get(id);
      if (!p) {
        p = { isExpanded: true };
        typesUI.set(id, p);
      }
    });
    return effortUI;
  }

  private createShadowProjects(projects: Project[]): Project[] {
    return structuredClone(projects).map((project) => ({
      ...project,
      hours: { ...project.hours },
      notes: { ...project.notes },
      review: { ...project.review },
    }));
  }

  private applyTimesheetEntries(
    projects: Project[],
    shadowProjects: Project[],
    timesheet: TimeSheet,
    weekDays: string[],
  ): void {
    for (let proj of projects) {
      for (let day of weekDays) {
        const entry = timesheet.timeEntries?.find(
          (e) =>
            e.dateEntry?.split('T')[0] == day &&
            e.projectId == proj.id &&
            e.timeTypeId == proj.ui.timeTypeId,
        );
        const shadowProj = shadowProjects.find(
          (sp) => sp.ui.virtualId == proj.ui.virtualId,
        );
        if (!shadowProj) {
          console.error(`Couldn't find shadow project for project ${proj.id}.`);
          continue;
        }
        shadowProj.review[day] = proj.review[day] = {
          isPleaseReview: entry?.isPleaseReview ?? false,
        };
        shadowProj.hours[day] = proj.hours[day] = TimeDateUtils.getHours(
          entry?.recordedTime,
        );
        shadowProj.notes[day] = proj.notes[day] = entry?.notes ?? '';
      }
    }
  }

  private expandReviewedAndSubmittedRows(
    projects: Project[],
    effortUI: { [id: number]: EffortUI },
    typesUI: Map<number, TypesUI>,
    isSubmitted: boolean,
    weekStart?: string,
    currentWeekStart?: string,
  ): void {
    for (let proj of projects) {
      const anyReview = Object.values(proj.review).some(
        (review) => review.isPleaseReview,
      );
      const anyHours = Object.values(proj.hours).some(
        (hours) => parseFloat(hours) > 0,
      );
      effortUI[proj.effort.id] ??= {};
      if (
        (anyReview || (anyHours && isSubmitted)) &&
        currentWeekStart != weekStart &&
        !proj.ui.isPinned
      ) {
        effortUI[proj.effort.id].isExpanded = true;
        const id = proj.id!;
        let p = typesUI.get(id);
        if (!p) {
          p = {};
          typesUI.set(id, p);
        }
        p.isExpanded = true;
      } else if (isSubmitted) {
        effortUI[proj.effort.id].isExpanded ||= false;
      }
    }
  }

  private restorePreviousChanges(
    projects: Project[],
    previousProjects: Project[],
  ): void {
    for (let preProject of previousProjects) {
      const postProject = projects.find(
        (p) =>
          p.id == preProject.id && p.ui.timeTypeId == preProject.ui.timeTypeId,
      );
      if (!postProject) continue;
      for (const day of Object.keys(preProject.hours)) {
        if (isOfficialLeaveCell(postProject, day)) continue;
        postProject.hours[day] = preProject.hours[day];
        postProject.notes[day] = preProject.notes[day];
      }
    }
  }
}
